import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Fab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MovimentoFinanceiro } from '../../types';
import { apiService } from '../../services/api';

interface MovimentoFormData {
  tipo: 'entrada' | 'saida';
  descricao: string;
  valor: number;
  data: string;
  categoria: string;
}

const categorias = {
  entrada: ['Vendas', 'Serviços', 'Recebimentos', 'Outros'],
  saida: ['Materiais', 'Fornecedores', 'Despesas Operacionais', 'Salários', 'Impostos', 'Outros'],
};

const FluxoCaixa: React.FC = () => {
  const [movimentos, setMovimentos] = useState<MovimentoFinanceiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMovimento, setEditingMovimento] = useState<MovimentoFinanceiro | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // React Hook Form
  const { register, control, handleSubmit, reset, watch, formState: { errors }, setError, clearErrors } = useForm<MovimentoFormData>({
    defaultValues: {
      tipo: 'entrada',
      descricao: '',
      valor: 0,
      data: new Date().toISOString().split('T')[0],
      categoria: '',
    },
  });

  const tipoAtual = watch('tipo');

  const showMessage = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  useEffect(() => {
    loadMovimentos();
  }, []);

  const loadMovimentos = async () => {
    try {
      setLoading(true);
      const data = await apiService.getMovimentos();
      setMovimentos(data);
    } catch (error) {
      console.error('Erro ao carregar movimentos:', error);
      showMessage('Erro ao carregar movimentos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = useCallback((movimento?: MovimentoFinanceiro) => {
    if (movimento) {
      setEditingMovimento(movimento);
      reset({
        tipo: movimento.tipo,
        descricao: movimento.descricao,
        valor: movimento.valor,
        data: movimento.data,
        categoria: movimento.categoria,
      });
    } else {
      setEditingMovimento(null);
      reset({
        tipo: 'entrada',
        descricao: '',
        valor: 0,
        data: new Date().toISOString().split('T')[0],
        categoria: '',
      });
    }
    setDialogOpen(true);
  }, [reset]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingMovimento(null);
  }, []);

  const onSubmit = async (data: MovimentoFormData) => {
    clearErrors();

    let hasErrors = false;

    if (!data.descricao || data.descricao.trim() === '') {
      setError('descricao', { type: 'required', message: 'Descrição é obrigatória' });
      hasErrors = true;
    }

    if (!data.valor || data.valor <= 0) {
      setError('valor', { type: 'required', message: 'Valor deve ser maior que zero' });
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      const movimentoData = {
        ...data,
        valor: Number(data.valor),
      };

      if (editingMovimento) {
        await apiService.updateMovimento(editingMovimento.id, movimentoData);
        showMessage('Movimento atualizado com sucesso!', 'success');
      } else {
        await apiService.createMovimento(movimentoData);
        showMessage('Movimento criado com sucesso!', 'success');
      }
      handleCloseDialog();
      loadMovimentos();
    } catch (error: any) {
      console.error('Erro ao salvar movimento:', error);
      const errorMessage = error?.message || error?.error || 'Erro ao salvar movimento. Verifique os dados e tente novamente.';
      showMessage(errorMessage, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este movimento?')) {
      try {
        await apiService.deleteMovimento(id);
        showMessage('Movimento excluído com sucesso!', 'success');
        loadMovimentos();
      } catch (error: any) {
        console.error('Erro ao excluir movimento:', error);
        const errorMessage = error?.message || error?.error || 'Erro ao excluir movimento. Tente novamente.';
        showMessage(errorMessage, 'error');
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTotalEntradas = () => {
    return movimentos
      .filter(m => m.tipo === 'entrada')
      .reduce((sum, m) => sum + m.valor, 0);
  };

  const getTotalSaidas = () => {
    return movimentos
      .filter(m => m.tipo === 'saida')
      .reduce((sum, m) => sum + m.valor, 0);
  };

  const getSaldo = () => {
    return getTotalEntradas() - getTotalSaidas();
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'entrada' ? 'Entrada' : 'Saída'}
          color={params.value === 'entrada' ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    { field: 'descricao', headerName: 'Descrição', flex: 1, minWidth: 200 },
    {
      field: 'valor',
      headerName: 'Valor',
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'data',
      headerName: 'Data',
      width: 100,
      renderCell: (params) => formatDate(params.value),
    },
    { field: 'categoria', headerName: 'Categoria', width: 150 },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const MovimentoCard: React.FC<{ movimento: MovimentoFinanceiro }> = ({ movimento }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {movimento.tipo === 'entrada' ? (
              <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ mr: 1, color: 'error.main' }} />
            )}
            <Typography variant="h6" component="div">
              {movimento.descricao}
            </Typography>
          </Box>
          <Chip
            label={movimento.tipo === 'entrada' ? 'Entrada' : 'Saída'}
            color={movimento.tipo === 'entrada' ? 'success' : 'error'}
            size="small"
          />
        </Box>
        <Typography variant="h5" color={movimento.tipo === 'entrada' ? 'success.main' : 'error.main'} gutterBottom>
          {formatCurrency(movimento.valor)}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Data: {formatDate(movimento.data)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Categoria: {movimento.categoria}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleOpenDialog(movimento)}
        >
          Editar
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => handleDelete(movimento.id)}
        >
          Excluir
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Fluxo de Caixa
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Movimento
          </Button>
        )}
      </Box>

      {/* Cards de resumo */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" color="success.main">
                {formatCurrency(getTotalEntradas())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Entradas
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingDownIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h6" color="error.main">
                {formatCurrency(getTotalSaidas())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Saídas
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon
                sx={{
                  fontSize: 40,
                  color: getSaldo() >= 0 ? 'success.main' : 'error.main',
                  mb: 1
                }}
              />
              <Typography
                variant="h6"
                color={getSaldo() >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(getSaldo())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Saldo
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {isMobile ? (
        <>
          <Box sx={{ mb: 2 }}>
            {loading ? (
              <Typography>Carregando...</Typography>
            ) : (
              movimentos
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
                .map((movimento) => (
                  <MovimentoCard key={movimento.id} movimento={movimento} />
                ))
            )}
          </Box>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => handleOpenDialog()}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={movimentos}
            columns={columns}
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: 'data', sort: 'desc' }],
              },
            }}
            disableRowSelectionOnClick
          />
        </Paper>
      )}

      {/* Dialog para criar/editar movimento */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {editingMovimento ? 'Editar Movimento' : 'Novo Movimento'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" type="submit">
                  Salvar
                </Button>
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <Controller
                    name="tipo"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select {...field} label="Tipo">
                          <MenuItem value="entrada">Entrada</MenuItem>
                          <MenuItem value="saida">Saída</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    label="Data"
                    type="date"
                    {...register('data')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Descrição"
                required
                {...register('descricao')}
                error={!!errors.descricao}
                helperText={errors.descricao?.message}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    label="Valor"
                    type="number"
                    required
                    {...register('valor')}
                    error={!!errors.valor}
                    helperText={errors.valor?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <Controller
                    name="categoria"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Categoria</InputLabel>
                        <Select {...field} label="Categoria">
                          {categorias[tipoAtual].map((cat) => (
                            <MenuItem key={cat} value={cat}>
                              {cat}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </form>
      </Dialog>

      {/* Snackbar para mensagens de feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FluxoCaixa;
